import type { FC, ReactNode } from 'react'
import type { CartLine } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { MAX_QUANTITY } from '@/lib/forms'

interface CartApi {
    lines: Array<CartLine>
    count: number
    ready: boolean
    addItem: (productId: string, quantity?: number) => void
    setQuantity: (productId: string, quantity: number) => void
    removeItem: (productId: string) => void
    clear: () => void
}

interface CartProviderProps {
    children: ReactNode
}

const STORAGE_KEY = 'qogoz-cart'

const noop = (): void => {}

const CartContext = createContext<CartApi>({
    lines: [],
    count: 0,
    ready: false,
    addItem: noop,
    setQuantity: noop,
    removeItem: noop,
    clear: noop
})

export const useCart = (): CartApi => useContext(CartContext)

const clampQuantity = (quantity: number): number => Math.min(MAX_QUANTITY, Math.max(1, Math.round(quantity)))

const readStoredCart = (): Array<CartLine> => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        if (!raw) return []
        const parsed: unknown = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []
        return parsed
            .filter((line): line is CartLine => typeof line?.productId === 'string' && Number.isFinite(line?.quantity))
            .map(line => ({ productId: line.productId, quantity: clampQuantity(line.quantity) }))
    } catch {
        return []
    }
}

// The cart only remembers which products and how many. Names and prices are always read fresh from the shop.
export const CartProvider: FC<CartProviderProps> = ({ children }): ReactNode => {
    const [lines, setLines] = useState<Array<CartLine>>([])
    const [ready, setReady] = useState(false)
    const linesRef = useRef<Array<CartLine>>([])

    useEffect(() => {
        const stored = readStoredCart()
        linesRef.current = stored
        setLines(stored)
        setReady(true)
    }, [])

    const commit = useCallback((next: Array<CartLine>): void => {
        linesRef.current = next
        setLines(next)
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
        } catch {
            // Private browsing can refuse storage, the cart still works for this visit
        }
    }, [])

    const addItem = useCallback((productId: string, quantity = 1): void => {
        const current = linesRef.current
        const existing = current.find(line => line.productId === productId)
        const next = existing
            ? current.map(line => line.productId === productId ? { ...line, quantity: clampQuantity(line.quantity + quantity) } : line)
            : [...current, { productId, quantity: clampQuantity(quantity) }]
        commit(next)
    }, [commit])

    const setQuantity = useCallback((productId: string, quantity: number): void => {
        commit(linesRef.current.map(line => line.productId === productId ? { ...line, quantity: clampQuantity(quantity) } : line))
    }, [commit])

    const removeItem = useCallback((productId: string): void => {
        commit(linesRef.current.filter(line => line.productId !== productId))
    }, [commit])

    const clear = useCallback((): void => {
        commit([])
    }, [commit])

    const count = useMemo(() => lines.reduce((sum, line) => sum + line.quantity, 0), [lines])

    const api = useMemo<CartApi>(
        () => ({ lines, count, ready, addItem, setQuantity, removeItem, clear }),
        [lines, count, ready, addItem, setQuantity, removeItem, clear]
    )

    return (
        <CartContext.Provider value={api}>
            {children}
        </CartContext.Provider>
    )
}
